import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useSite } from '../../../hooks/useSiteConfig';
import { CmsButton, SectionShell, SectionHeader, RichText, isDarkBg, SHADCN_BTN, SHADCN_BTN_PRIMARY } from '../shared';

const FALLBACK = { phone: '', email: '', address: '', hours: 'Showroom visits by appointment' };
const lines = (s = '') => s.split(/\s·\s|\n/).map((l) => l.trim()).filter(Boolean);
const addressLines = (a = '') => { const i = a.indexOf(', '); return i > 0 ? [a.slice(0, i), a.slice(i + 2)] : [a]; };

const useContact = () => { const { globals } = useSite(); return { ...FALLBACK, ...(globals?.contact || {}), brand: globals?.brand_name || '' }; };
const Lines = ({ items, className }) => <p className={className}>{items.map((l, i) => <React.Fragment key={i}>{l}{i < items.length - 1 && <br />}</React.Fragment>)}</p>;

// Mirrors the coded Contact page "Contact Information" column (plain amber icons, medium labels, address/hours on separate lines).
export const ContactDetails = ({ dark, compact }) => {
  const c = useContact();
  const muted = dark ? 'text-neutral-300' : 'text-neutral-600';
  const tel = `tel:${(c.phone || '').replace(/\D/g, '')}`;
  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'} data-testid="contact-details">
      <div className="flex items-start gap-4"><MapPin className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" /><div><p className="font-medium">Visit Our Showroom</p><Lines className={`${muted} mt-1`} items={[c.brand, ...addressLines(c.address)].filter(Boolean)} /></div></div>
      {c.phone && <div className="flex items-start gap-4"><Phone className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" /><div><p className="font-medium">Phone</p><a href={tel} className={`${muted} mt-1 hover:text-amber-700`}>{c.phone}</a></div></div>}
      {c.email && <div className="flex items-start gap-4"><Mail className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" /><div><p className="font-medium">Email</p><a href={`mailto:${c.email}`} className={`${muted} mt-1 hover:text-amber-700`}>{c.email}</a></div></div>}
      {c.hours && <div className="flex items-start gap-4"><Clock className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" /><div><p className="font-medium">Business Hours</p><Lines className={`${muted} mt-1`} items={lines(c.hours)} /></div></div>}
    </div>
  );
};

// Mirrors the coded About page "Brooklyn Location" block (no hours, company name bold, phone/email centered rows).
const ShowroomDetails = ({ dark }) => {
  const c = useContact();
  const muted = dark ? 'text-neutral-300' : 'text-neutral-600';
  return (
    <div className="space-y-4" data-testid="contact-details">
      <div className="flex items-start gap-4"><MapPin className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" /><div><p className={`font-medium ${dark ? 'text-white' : 'text-neutral-900'}`}>{c.brand}</p><Lines className={muted} items={addressLines(c.address)} /></div></div>
      {c.phone && <div className="flex items-center gap-4"><Phone className="w-5 h-5 text-amber-700 flex-shrink-0" /><p className={muted}>{c.phone}</p></div>}
      {c.email && <div className="flex items-center gap-4"><Mail className="w-5 h-5 text-amber-700 flex-shrink-0" /><p className={muted}>{c.email}</p></div>}
    </div>
  );
};

export const ContactInfoSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'cards';
  if (layout === 'with_map') {
    return (
      <SectionShell section={section}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            {p.title && <h2 className={`mt-4 mb-6 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
            <RichText html={p.body} className={`mb-8 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
            <ShowroomDetails dark={dark} />
            {(p.primary_button?.text || p.secondary_button?.text) && (
              <div className="mt-8 flex gap-4">
                <CmsButton button={p.primary_button} className={`${SHADCN_BTN} ${SHADCN_BTN_PRIMARY}`} icon={false} />
                <CmsButton button={p.secondary_button} className={`${SHADCN_BTN} ${SHADCN_BTN_PRIMARY} border border-neutral-900`} icon={false} />
              </div>
            )}
          </div>
          <div className="aspect-square bg-neutral-200 [&_iframe]:w-full [&_iframe]:h-full" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.map_embed || '', { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'loading', 'referrerpolicy', 'src', 'style', 'width', 'height'] }) }} />
        </div>
      </SectionShell>
    );
  }
  const buttons = (p.primary_button?.text || p.secondary_button?.text) && (
    <div className="mt-8 flex flex-wrap gap-4"><CmsButton button={p.primary_button} variant={dark ? 'light' : 'primary'} /><CmsButton button={p.secondary_button} variant={dark ? 'outline_light' : 'secondary'} /></div>
  );
  return (
    <SectionShell section={section} container="lg">
      <SectionHeader props={p} style={{ align: layout === 'cards' ? 'center' : 'left' }} dark={dark} />
      {layout === 'cards' ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 [&>div]:contents"><ContactDetails dark={dark} /></div>
      ) : <ContactDetails dark={dark} />}
      {buttons && <div className={layout === 'cards' ? 'flex justify-center' : ''}>{buttons}</div>}
    </SectionShell>
  );
};
